import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/apiService';
import { setTokens } from '../utils/authTokenStorage';
import { ErrorHandler } from '../utils/errorHandler';

// Parse Pydantic validation errors into user-friendly messages
const parseValidationErrors = (errors) => {
  if (!Array.isArray(errors) || errors.length === 0) {
    return 'Please check your input and try again';
  }
  
  const firstError = errors[0];
  if (!firstError || !firstError.msg) {
    return 'Please check your input and try again';
  }
  
  // Map specific validation errors to user-friendly messages
  const errorMappings = {
    // String length errors
    'String should have at least 8 characters': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
    'String should have at least 1 character': 'This field cannot be empty',
    
    // Email validation errors
    'value is not a valid email address: An email address must have an @-sign.': 'Please enter a valid email address',
    'value is not a valid email address': 'Please enter a valid email address',
    
    // Phone validation errors
    'Invalid phone number. Enter a valid phone number with country code (e.g. +1 for US).': 'Enter a valid phone number with country code (e.g. +1 for US)',
    'Value error, Invalid phone number. Enter a valid phone number with country code (e.g. +1 for US).': 'Enter a valid phone number with country code (e.g. +1 for US)',
    
    // Password validation errors
    'Password must be at least 8 characters long': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
    'Password must contain at least one uppercase letter': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
    'Password must contain at least one lowercase letter': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
    'Password must contain at least one digit': 'Password must be at least 8 characters long and contain at least one uppercase letter, one lowercase letter, and one digit',
    
    // Enum validation errors
    "Input should be 'runner' or 'runclub'": 'Please select a valid user type',
    
    // Name validation errors
    'Name cannot be empty': 'Name is required',
    
    // URL validation errors
    'Invalid URL format': 'Please enter a valid URL',
    
    // General value errors
    'Value error': 'Invalid input provided'
  };
  
  // Check for exact matches first
  if (errorMappings[firstError.msg]) {
    return errorMappings[firstError.msg];
  }
  
  // Check for partial matches
  for (const [key, value] of Object.entries(errorMappings)) {
    if (firstError.msg.includes(key)) {
      return value;
    }
  }
  
  // Return the original message if no mapping found
  return firstError.msg;
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AUTH_RETRY_DELAY_MS = 400;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const initialCheckDone = React.useRef(false);

  const fetchUser = useCallback(async (isRetry = false) => {
    try {
      // With httpOnly cookies, tokens are handled automatically by the browser
      // Just fetch user data - backend will validate cookies
      // If access token is expired, interceptor will try to refresh it automatically
      const response = await apiService.auth.getCurrentUser();
      setUser(response);
    } catch (error) {
      const is401 = error?.response?.status === 401;
      // On initial load, retry once after a short delay (cookies can be delayed on first request after refresh)
      if (is401 && !isRetry && !initialCheckDone.current) {
        initialCheckDone.current = true;
        await new Promise((r) => setTimeout(r, AUTH_RETRY_DELAY_MS));
        await fetchUser(true);
        return;
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initialCheckDone.current = false;
    // With httpOnly cookies, check authentication by fetching user
    // Backend will validate cookies automatically
    // Safety timeout - ensure loading is always set to false after 5 seconds
    const timeout = setTimeout(() => {
      setLoading(false);
    }, 5000);

    fetchUser().finally(() => {
      clearTimeout(timeout);
    });

    return () => clearTimeout(timeout);
  }, [fetchUser]);

  const login = async (emailOrPhone, password, userType) => {
    try {
      const response = await apiService.auth.login(emailOrPhone, password, userType);
      const userData = response?.user;
      if (!userData) {
        throw new Error('Invalid response from server');
      }
      if (response?.access_token) {
        setTokens(response.access_token, response.refresh_token);
      }
      setUser(userData);
      return { success: true, user: userData };
    } catch (error) {
      
      // Enhanced error handling with more specific messages
      let errorMessage = 'Login failed';
      
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail || '';
        
        // Handle Pydantic validation errors (422 status with array of errors)
        if (status === 422 && Array.isArray(detail)) {
          errorMessage = parseValidationErrors(detail);
        } else {
          switch (status) {
          case 401:
            // Use the specific error message from the backend
            errorMessage = detail || 'Invalid email/phone or password';
            break;
          case 403:
            // Surface the backend message (e.g. the early-access / admin-only web gate)
            errorMessage = detail || 'Account access denied';
            break;
          case 404:
            // Check if it's a 404 on the API endpoint itself (server not running)
            if (error.config?.url?.includes('/api/auth/login')) {
              errorMessage = 'Server not responding. Please ensure the backend server is running on port 8000.';
            } else {
              errorMessage = 'User not found';
            }
            break;
          case 429:
            errorMessage = 'Too many login attempts. Please try again later';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later';
            break;
          default:
            errorMessage = detail || `Login failed (${status})`;
          }
        }
      } else if (error.request) {
        errorMessage = 'Unable to connect to server. Please check your internet connection';
      } else {
        errorMessage = 'An unexpected error occurred during login';
      }
      
      return { 
        success: false, 
        error: errorMessage,
        details: {
          status: error.response?.status,
          code: error.code,
          message: error.message
        }
      };
    }
  };

  const register = async (email, password, name, phone, userType, city, state, runClubName = null) => {
    try {
      const response = await apiService.auth.register({
        email,
        password,
        name,
        phone,
        user_type: userType,
        city,
        state,
        run_club_name: runClubName,
      });
      const userData = response?.user;
      if (!userData) {
        throw new Error('Invalid response from server');
      }
      if (response?.access_token) {
        setTokens(response.access_token, response.refresh_token);
      }
      setUser(userData);
      return { success: true };
    } catch (error) {
      let errorMessage = 'Registration failed';
      
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const detail = error.response.data?.detail || '';
        
        // Handle Pydantic validation errors (422 status with array of errors)
        if (status === 422 && Array.isArray(detail)) {
          errorMessage = parseValidationErrors(detail);
        } else {
          switch (status) {
            case 400:
              if (detail.includes('Email already registered')) {
                errorMessage = 'This email is already registered. Please try logging in instead.';
              } else if (detail.includes('Invalid email format')) {
                errorMessage = 'Please enter a valid email address';
              } else if (detail.includes('Password')) {
                errorMessage = 'Password requirements not met';
              } else {
                errorMessage = detail || 'Invalid registration data';
              }
              break;
            case 422:
              errorMessage = 'Please check your input and try again';
              break;
            case 500:
              errorMessage = 'Server error. Please try again later';
              break;
            default:
              errorMessage = detail || 'Registration failed';
          }
        }
      } else if (error.request) {
        // Network error
        errorMessage = 'Unable to connect to server. Please check your internet connection';
      } else {
        // Other error
        errorMessage = 'An unexpected error occurred';
      }
      
      return { 
        success: false, 
        error: errorMessage
      };
    }
  };

  const logout = async () => {
    try {
      // Call logout endpoint to clear cookies on server
      await apiService.auth.logout();
    } catch (error) {
      // Even if logout fails, clear user state
      ErrorHandler.logError('AuthContext - logout', error);
    } finally {
      setUser(null);
    }
  };


  const updateEmail = async (email) => {
    try {
      const response = await apiService.users.updateEmail({ email });
      
      setUser(response);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: Array.isArray(error.response?.data?.detail) 
          ? error.response.data.detail[0]?.msg || 'Email update failed'
          : error.response?.data?.detail || 'Email update failed' 
      };
    }
  };

  const updateProfilePicture = async (profilePicture) => {
    try {
      const response = await apiService.users.updateProfilePicture({ profile_picture: profilePicture });
      
      setUser(response);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: Array.isArray(error.response?.data?.detail) 
          ? error.response.data.detail[0]?.msg || 'Profile picture update failed'
          : error.response?.data?.detail || 'Profile picture update failed' 
      };
    }
  };


  const loginWithGoogle = async (idToken, userType = 'runner') => {
    try {
      const response = await apiService.auth.google(idToken, userType);
      const userData = response?.user;
      if (!userData) {
        throw new Error('Invalid response from server');
      }
      if (response?.access_token) {
        setTokens(response.access_token, response.refresh_token);
      }
      setUser(userData);
      return { success: true };
    } catch (error) {
      // Enhanced error handling with more specific messages
      let errorMessage = 'Google Sign-In failed';
      
      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail || '';
        
        // Handle Pydantic validation errors (422 status with array of errors)
        if (status === 422 && Array.isArray(detail)) {
          errorMessage = parseValidationErrors(detail);
        } else {
          switch (status) {
          case 401:
            errorMessage = detail || 'Invalid Google credentials';
            break;
          case 403:
            errorMessage = 'Account access denied';
            break;
          case 429:
            errorMessage = 'Too many sign-in attempts. Please try again later';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later';
            break;
          default:
            errorMessage = detail || `Google Sign-In failed (${status})`;
          }
        }
      } else if (error.request) {
        errorMessage = 'Network error. Please check your connection and try again.';
      } else {
        errorMessage = error.message || 'Google Sign-In failed. Please try again.';
      }
      
      toast.error(errorMessage);
      throw error;
    }
  };

  const value = {
    user,
    setUser,
    login,
    register,
    logout,
    loginWithGoogle,
    updateEmail,
    updateProfilePicture,
    loading,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};